import React, { useEffect, useMemo, useState } from 'react';

import { apiFetch } from './api';

const POLL_INTERVAL_MS = 5000;

function formatTimestamp(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat('sv-SE', {
    timeStyle: 'short'
  }).format(date);
}

function getJobTone(job) {
  if (!job) {
    return 'muted';
  }

  if (job.status !== 'completed') {
    return 'info';
  }

  if (job.conclusion === 'success') {
    return 'success';
  }

  return 'error';
}

function getStepLabel(baseLabel, job) {
  if (!job) {
    return `${baseLabel} väntar`;
  }

  if (job.status !== 'completed') {
    return `${baseLabel} påbörjad`;
  }

  if (job.conclusion === 'success') {
    return `${baseLabel} klart`;
  }

  return `${baseLabel} misslyckades`;
}

function findJobByKeyword(run, keyword) {
  return run.jobs.find((job) => job.name && job.name.toLowerCase().includes(keyword));
}

function StepRow({ label, job }) {
  const timeLabel = formatTimestamp(job?.completedAt || job?.startedAt || job?.updatedAt);
  const tone = getJobTone(job);

  return (
    <div className={`workflow-step workflow-step--${tone}`}>
      <span>{getStepLabel(label, job)}</span>
      {timeLabel ? <span className="workflow-step__time">{timeLabel}</span> : null}
    </div>
  );
}

export default function WorkflowStatusPanel({ siteId, accessToken, commitSha, visible }) {
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!visible || !commitSha) {
      setRuns([]);
      setError('');
      setLoading(false);
      return undefined;
    }

    let active = true;
    let timeoutId;

    async function loadRuns() {
      if (active) {
        setLoading(true);
        setError('');
      }

      try {
        const payload = await apiFetch(`/sites/${siteId}/actions/runs?commitSha=${encodeURIComponent(commitSha)}`, {
          accessToken
        });

        if (!active) {
          return;
        }

        const nextRuns = payload.data?.runs || [];
        setRuns(nextRuns);

        const shouldKeepPolling = nextRuns.length === 0 || nextRuns.some((run) => run.status !== 'completed');

        if (shouldKeepPolling) {
          timeoutId = window.setTimeout(() => {
            setReloadToken((current) => current + 1);
          }, POLL_INTERVAL_MS);
        }
      } catch (requestError) {
        if (!active) {
          return;
        }

        setError(requestError.message || 'Kunde inte hämta GitHub Actions-status.');
        timeoutId = window.setTimeout(() => {
          setReloadToken((current) => current + 1);
        }, POLL_INTERVAL_MS);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadRuns();

    return () => {
      active = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [accessToken, commitSha, reloadToken, siteId, visible]);

  const latestRun = useMemo(() => runs[0] || null, [runs]);
  const buildJob = useMemo(() => (latestRun ? findJobByKeyword(latestRun, 'build') : null), [latestRun]);
  const deployJob = useMemo(() => (latestRun ? findJobByKeyword(latestRun, 'deploy') : null), [latestRun]);

  if (!visible || !commitSha) {
    return null;
  }

  if (error) {
    return <p className="feedback feedback--error">{error}</p>;
  }

  if (!latestRun) {
    return (
      <div className="workflow-status workflow-status--compact shell-subcard" aria-live="polite">
        <p className="workflow-status__empty">
          {loading
            ? 'Väntar på att build ska starta...'
            : 'Commit skapad. Väntar på att GitHub Actions ska starta.'}
        </p>
      </div>
    );
  }

  return (
    <section className="workflow-status workflow-status--compact shell-subcard" aria-live="polite">
      <div className="workflow-status__compact-header">
        <span className="muted-text">GitHub Actions för commit {commitSha.slice(0, 7)}</span>
        {latestRun.htmlUrl ? (
          <a className="workflow-job-item__link" href={latestRun.htmlUrl} target="_blank" rel="noreferrer">
            Öppna i GitHub
          </a>
        ) : null}
      </div>

      <div className="workflow-status__steps">
        <StepRow label="Build" job={buildJob} />
        {deployJob ? <StepRow label="Deploy" job={deployJob} /> : null}
      </div>
    </section>
  );
}